import React from "react";
import { Zap, LineChart as LineIcon, BarChart3, Rocket, CheckCircle2, XCircle, Pause } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";

const C = {
    border: "#1B2A3A", text: "#E6EDF6", sub: "#A7B4C6", dim: "#6F7D92",
    accent: "#2EE6D6", bull: "#2BE38B", bear: "#FF5C6C", warn: "#FFB020", info: "#3DA7FF",
};

const Head = ({ icon: Icon, title, right }) => (
    <div className="px-4 py-3 border-b flex items-center gap-2" style={{ borderColor: C.border }}>
        <Icon size={14} color={C.accent} />
        <span className="text-[11px] uppercase tracking-[0.16em]" style={{ color: C.sub }}>{title}</span>
        {right && <span className="ml-auto">{right}</span>}
    </div>
);

const actionColor = (a) => {
    if (!a) return C.info;
    if (a.includes("PAUSE") || a.includes("DERISK") || a.includes("STOP_LOSS")
        || a.includes("FAST_MOVE_GUARD") || a.includes("GUARD_CANCEL")
        || a.includes("PAPER_RESET")) return C.bear;
    if (a.includes("RESUME")) return C.bull;
    if (a.includes("ADAPT") || a.includes("PUMP_GUARD") || a.includes("GUARD_TUNE")) return C.warn;
    if (a.includes("GUARD_CONFIG")) return C.accent;
    return C.info;
};

export const ActionFeedPanel = ({ actions }) => {
    const items = (actions && actions.items) || [];
    const paused = actions && actions.pause_buys;
    return (
        <div className="panel" data-testid="sg-action-feed">
            <Head icon={Zap} title="AI Trade Manager — Executed Actions"
                right={<span className="text-[10px] px-2 py-0.5 rounded font-mono inline-flex items-center gap-1"
                    style={{ color: paused ? C.bear : C.bull, background: "#13202D" }}>
                    {paused && <Pause size={10} />} buys {paused ? "PAUSED" : "active"}
                </span>} />
            <div className="p-3 space-y-1.5 max-h-[280px] overflow-y-auto">
                {items.length === 0 && <div className="text-[11px]" style={{ color: "#3A4759" }}>No actions yet — the bot is within calm/observe band.</div>}
                {items.map((a, index) => (
                    <div
                        key={a.id || a.seq || `${a.ts_ms}-${a.action}-${a.detail}-${index}`}
                        className="rounded-md p-2.5"
                        style={{ background: "#0B1219", border: `1px solid ${actionColor(a.action)}30` }}
                        data-testid={`sg-action-row-${index}`}
                    >
                        <div className="flex items-center gap-2">
                            <span className="font-mono text-[12px] font-semibold" style={{ color: actionColor(a.action) }}>{a.action}</span>
                            <span className="ml-auto text-[9px] uppercase px-1.5 py-0.5 rounded" style={{ color: C.sub, background: "#13202D" }}>
                                {a.band} · {Number(a.confidence).toFixed(0)}%
                            </span>
                        </div>
                        <div className="text-[11px] mt-1" style={{ color: C.sub }}>{a.detail}</div>
                        <div className="text-[10px] mt-0.5" style={{ color: C.dim }}>regime {a.regime} · adverse {a.reversal_risk}%</div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export const EquityComparePanel = ({ equity }) => {
    const e = equity || {};
    const base = {
        smartgrid: e.smartgrid?.[0]?.[1],
        griddesk: e.griddesk?.[0]?.[1],
        best_twin: e.best_twin?.[0]?.[1],
    };
    const len = Math.max((e.smartgrid || []).length, (e.griddesk || []).length, (e.best_twin || []).length);
    const data = [];
    const toPct = (v, b) => (b && v != null ? ((v / b - 1) * 100) : undefined);
    for (let i = 0; i < len; i++) {
        const row = { i };
        if (e.smartgrid?.[i]) row.smartgrid = toPct(e.smartgrid[i][1], base.smartgrid);
        if (e.griddesk?.[i]) row.griddesk = toPct(e.griddesk[i][1], base.griddesk);
        if (e.best_twin?.[i]) row.best_twin = toPct(e.best_twin[i][1], base.best_twin);
        data.push(row);
    }
    const values = data.flatMap((row) => [row.smartgrid, row.griddesk, row.best_twin])
        .filter((value) => Number.isFinite(value));
    const lo = values.length ? Math.min(...values) : -0.01;
    const hi = values.length ? Math.max(...values) : 0.01;
    const pad = Math.max((hi - lo) * 0.1, 0.01);
    const equityDomain = [lo - pad, hi + pad];
    return (
        <div className="panel" data-testid="sg-equity-compare">
            <Head icon={LineIcon} title="Equity Comparison — % return (SmartGrid vs GridDesk vs Best Twin)" />
            <div className="p-3 w-full min-w-0" style={{ height: 240 }}>
                {data.length < 2 ? (
                    <div className="h-full flex items-center justify-center text-[11px]" style={{ color: "#3A4759" }}>
                        Collecting equity samples (every 60s)…
                    </div>
                ) : (
                    <ResponsiveContainer width="100%" height={216} minWidth={1} minHeight={1} debounce={50}>
                        <LineChart data={data} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
                            <XAxis dataKey="i" hide />
                            <YAxis domain={equityDomain} tick={{ fill: C.dim, fontSize: 10 }} width={48}
                                tickFormatter={(v) => `${v.toFixed(2)}%`} />
                            <Tooltip contentStyle={{ background: "#0B1219", border: `1px solid ${C.border}`, fontSize: 11 }}
                                labelStyle={{ color: C.dim }} formatter={(v) => `${Number(v).toFixed(3)}%`} />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            <Line type="monotone" dataKey="smartgrid" stroke={C.accent} dot={false} strokeWidth={2} isAnimationActive={false} />
                            <Line type="monotone" dataKey="griddesk" stroke={C.info} dot={false} strokeWidth={2} isAnimationActive={false} />
                            <Line type="monotone" dataKey="best_twin" stroke={C.warn} dot={false} strokeWidth={1.5} strokeDasharray="4 3" isAnimationActive={false} />
                        </LineChart>
                    </ResponsiveContainer>
                )}
            </div>
        </div>
    );
};

export const AnalyticsPanel = ({ analytics }) => {
    const a = analytics || {};
    const ranked = a.hypothesis_reliability_ranked || [];
    const o = a.online_ml || {};
    const readiness = o.training_readiness || {};
    return (
        <div className="panel" data-testid="sg-analytics-panel">
            <Head icon={BarChart3} title="Learning Analytics" />
            <div className="p-4">
                <div className="grid grid-cols-3 gap-3 mb-3">
                    <div><div className="text-[10px] uppercase" style={{ color: C.dim }}>Expected Value</div>
                        <div className="font-mono text-sm" style={{ color: (o.ev ?? 0) >= 0 ? C.bull : C.bear }}>{o.ev == null ? "—" : `${(o.ev * 100).toFixed(3)}%`}</div></div>
                    <div><div className="text-[10px] uppercase" style={{ color: C.dim }}>WF Streak</div>
                        <div className="font-mono text-sm" style={{ color: C.accent }}>{a.walk_forward_streak ?? 0}</div></div>
                    <div><div className="text-[10px] uppercase" style={{ color: C.dim }}>Generation</div>
                        <div className="font-mono text-sm" style={{ color: C.info }}>{a.twin_generation ?? 0}</div></div>
                </div>
                <div
                    className="mb-3 rounded-md px-2.5 py-2 text-[10px]"
                    style={{
                        color: readiness.ready ? C.bull : C.warn,
                        background: "#0B1219",
                        border: `1px solid ${readiness.ready ? C.bull : C.warn}35`,
                    }}
                    data-testid="sg-ml-training-readiness"
                >
                    {readiness.ready ? "TRAINING EVIDENCE READY" : "TRAINING EVIDENCE NOT READY"}
                    <span style={{ color: C.dim }}>
                        {` · independent ${readiness.global_independent ?? 0}/${readiness.global_required ?? 2000}`}
                        {` · crash ${readiness.crash_independent ?? 0}/${readiness.crash_required ?? 100}`}
                        {` · pump ${readiness.pump_independent ?? 0}/${readiness.pump_required ?? 100}`}
                    </span>
                </div>
                <div className="text-[10px] uppercase tracking-[0.12em] mb-1.5" style={{ color: C.dim }}>Most reliable hypotheses</div>
                <div className="space-y-1">
                    {ranked.slice(0, 5).map((h) => (
                        <div key={h.type} className="flex items-center gap-2 text-[11px]">
                            <span style={{ color: C.sub }}>{h.type}</span>
                            <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: "#13202D" }}>
                                <div style={{ width: `${(h.win_rate * 100).toFixed(0)}%`, height: "100%", background: h.win_rate >= 0.5 ? C.bull : C.bear }} />
                            </div>
                            <span className="font-mono" style={{ color: C.text }}>{(h.win_rate * 100).toFixed(0)}%</span>
                            <span className="font-mono" style={{ color: C.dim }}>({h.samples})</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

const Check = ({ ok, label }) => (
    <div className="flex items-center gap-2 text-[12px]" data-testid="sg-golive-check">
        {ok ? <CheckCircle2 size={14} color={C.bull} /> : <XCircle size={14} color={C.dim} />}
        <span style={{ color: ok ? C.text : C.sub }}>{label}</span>
    </div>
);

export const GoLiveChecklist = ({ status, botStatus, autotune, risk }) => {
    const checks = [
        { ok: !!botStatus?.available, label: "SmartGrid bot initialised" },
        { ok: botStatus?.mode === "paper" && botStatus?.running, label: "Validated in PAPER (running now)" },
        { ok: (status?.data_sufficiency ?? 0) > 0.5, label: "Live feature feed coverage >50%" },
        { ok: !!status?.online_ml?.training_readiness?.ready, label: "Independent ML regime evidence ready" },
        { ok: !!autotune?.gates?.walk_forward_stable, label: "Walk-forward stable winner" },
        { ok: !risk?.breaker_engaged, label: "No risk circuit-breakers engaged" },
        { ok: !!autotune?.recommend, label: "Auto-tune promotion approved" },
    ];
    const ready = checks.every((c) => c.ok);
    return (
        <div className="panel" data-testid="sg-golive-checklist">
            <Head icon={Rocket} title="LIVE Go-Live Checklist"
                right={<span className="text-[10px] px-2 py-0.5 rounded font-mono"
                    style={{ color: ready ? C.bull : C.warn, background: "#13202D" }}>
                    {ready ? "READY" : "NOT READY"}
                </span>} />
            <div className="p-4 space-y-2">
                {checks.map((c) => <Check key={c.label} ok={c.ok} label={c.label} />)}
                <div className="text-[10px] mt-2" style={{ color: C.dim }}>
                    Flip the bot to LIVE only after these clear — it trades real funds on your shared wallet.
                </div>
            </div>
        </div>
    );
};
